import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
interface Student {
  id: number;
  name: string;
  level: string;
  grade: number;
  notes?: string;
  flaggedForReview?: boolean;
}
interface EditStudentPerformanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedStudent: Student) => void;
  student: Student;
  darkMode: boolean;
}
export const EditStudentPerformanceModal: React.FC<EditStudentPerformanceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  student,
  darkMode
}) => {
  const [level, setLevel] = useState(student.level);
  const [grade, setGrade] = useState(student.grade);
  const [notes, setNotes] = useState(student.notes || '');
  const [flaggedForReview, setFlaggedForReview] = useState(student.flaggedForReview || false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...student,
      level,
      grade,
      notes,
      flaggedForReview
    });
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1256">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-1257">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-1258">
          <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-1259"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-1260">
          &#8203;
        </span>
        <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-1261">
          <div className="px-6 py-5 flex items-center justify-between border-b border-gray-700" data-id="element-1262">
            <h3 className="text-xl font-medium" data-id="element-1263">
              Edit Student Performance Notes
            </h3>
            <button onClick={onClose} className={`rounded-md ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} p-1.5 transition-colors`} data-id="element-1264">
              <XIcon className="h-5 w-5" data-id="element-1265" />
            </button>
          </div>
          <form onSubmit={handleSubmit} data-id="element-1266">
            <div className="p-6 space-y-4" data-id="element-1267">
              <div data-id="element-1268">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1269">
                  Student Name
                </label>
                <input type="text" value={student.name} disabled className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-700 border-navy-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-700'} focus:ring-deep-pink focus:border-deep-pink cursor-not-allowed opacity-70`} data-id="element-1270" />
              </div>
              <div data-id="element-1271">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1272">
                  Current Level
                </label>
                <select value={level} onChange={e => setLevel(e.target.value)} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-1273">
                  <option value="Beginner" data-id="element-1274">Beginner</option>
                  <option value="Intermediate" data-id="element-1275">Intermediate</option>
                  <option value="Advanced" data-id="element-1276">Advanced</option>
                  <option value="Expert" data-id="element-1277">Expert</option>
                </select>
              </div>
              <div data-id="element-1278">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1279">
                  Grade Override (0-100)
                </label>
                <input type="number" min="0" max="100" value={grade} onChange={e => setGrade(parseInt(e.target.value) || 0)} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-1280" />
                <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1281">
                  Grade conversion:
                  {grade >= 90 ? ' A (90-100%)' : grade >= 80 ? ' B (80-89%)' : grade >= 70 ? ' C (70-79%)' : grade >= 60 ? ' D (60-69%)' : ' F (0-59%)'}
                </div>
              </div>
              <div data-id="element-1282">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1283">
                  Custom Notes
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} placeholder="Add performance notes, observations, or areas for improvement..." data-id="element-1284"></textarea>
              </div>
              <div className="flex items-center" data-id="element-1285">
                <input type="checkbox" id="flagForReview" checked={flaggedForReview} onChange={e => setFlaggedForReview(e.target.checked)} className={`h-4 w-4 rounded ${darkMode ? 'bg-navy-900 border-navy-700 text-deep-pink focus:ring-deep-pink' : 'bg-white border-gray-300 text-deep-pink focus:ring-deep-pink'}`} data-id="element-1286" />
                <label htmlFor="flagForReview" className={`ml-2 block text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1287">
                  Flag for review
                </label>
              </div>
            </div>
            <div className={`px-6 py-4 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} flex justify-end space-x-3`} data-id="element-1288">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-1289">
                Cancel
              </button>
              <button type="submit" className="bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg transition-colors" data-id="element-1290">
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
};